import { CheckIcon } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import {
  ModelSelector,
  ModelSelectorContent,
  ModelSelectorEmpty,
  ModelSelectorGroup,
  ModelSelectorInput,
  ModelSelectorItem,
  ModelSelectorList,
  ModelSelectorLogo,
  ModelSelectorName,
  ModelSelectorTrigger,
} from '@/components/ai-elements/model-selector'
import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputAttachment,
  PromptInputAttachments,
  PromptInputBody,
  PromptInputButton,
  PromptInputFooter,
  type PromptInputMessage,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
} from '@/components/ai-elements/prompt-input'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { DEFAULT_MODEL } from '@/lib/constants/chat-constants'
import { useCredits } from '@/lib/hooks/use-credits'
import { languageModels } from '@/lib/models'

function IncognitoIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M17.5,12 C19.9852814,12 22,14.0147186 22,16.5 C22,18.9852814 19.9852814,21 17.5,21 C15.3591076,21 13.5674006,19.5049595 13.1119514,17.5019509 L10.8880486,17.5019509 C10.4325994,19.5049595 8.64089238,21 6.5,21 C4.01471863,21 2,18.9852814 2,16.5 C2,14.0147186 4.01471863,12 6.5,12 C8.81637876,12 10.7239814,13.7501788 10.9725684,16.000297 L13.0274316,16.000297 C13.2760186,13.7501788 15.1836212,12 17.5,12 Z M6.5,13.5 C4.84314575,13.5 3.5,14.8431458 3.5,16.5 C3.5,18.1568542 4.84314575,19.5 6.5,19.5 C8.15685425,19.5 9.5,18.1568542 9.5,16.5 C9.5,14.8431458 8.15685425,13.5 6.5,13.5 Z M17.5,13.5 C15.8431458,13.5 14.5,14.8431458 14.5,16.5 C14.5,18.1568542 15.8431458,19.5 17.5,19.5 C19.1568542,19.5 20.5,18.1568542 20.5,16.5 C20.5,14.8431458 19.1568542,13.5 17.5,13.5 Z M12,9.25 C15.3893368,9.25 18.5301001,9.58954198 21.4217795,10.2699371 C21.8249821,10.3648083 22.0749341,10.7685769 21.9800629,11.1717795 C21.8851917,11.5749821 21.4814231,11.8249341 21.0782205,11.7300629 C18.3032332,11.0771247 15.2773298,10.75 12,10.75 C8.72267018,10.75 5.69676679,11.0771247 2.9217795,11.7300629 C2.51857691,11.8249341 2.11480832,11.5749821 2.01993712,11.1717795 C1.92506593,10.7685769 2.17501791,10.3648083 2.5782205,10.2699371 C5.46989988,9.58954198 8.61066315,9.25 12,9.25 Z M15.7002538,3.25 C16.7230952,3.25 17.6556413,3.81693564 18.1297937,4.71158956 L18.2132356,4.88311922 L19.6853587,8.19539615 C19.8535867,8.57390929 19.683117,9.0171306 19.3046038,9.18535866 C18.9576335,9.33956772 18.5562903,9.20917654 18.3622308,8.89482229 L18.3146413,8.80460385 L16.8425183,5.49232692 C16.6601304,5.08195418 16.2735894,4.80422037 15.8336777,4.75711483 L15.7002538,4.75 L8.29974618,4.75 C7.85066809,4.75 7.43988259,4.99042719 7.21817192,5.37329225 L7.15748174,5.49232692 L5.68535866,8.80460385 C5.5171306,9.18311699 5.07390929,9.35358672 4.69539615,9.18535866 C4.34842577,9.03114961 4.17626965,8.64586983 4.27956492,8.29117594 L4.31464134,8.19539615 L5.78676442,4.88311922 C6.20217965,3.94843495 7.09899484,3.32651789 8.10911143,3.25658537 L8.29974618,3.25 L15.7002538,3.25 Z"
        fill="currentColor"
      />
    </svg>
  )
}

interface ChatInputProps {
  loading: boolean
  onSend: (
    model: string,
    text?: string,
    files?: PromptInputMessage['files']
  ) => Promise<void> | void
  onStop?: () => void
  isIncognito: boolean
  onToggleIncognito: () => void
}

const MIN_CREDITS_REQUIRED = 0.01
export default function ChatInput({
  loading,
  onSend,
  onStop,
  isIncognito,
  onToggleIncognito,
}: ChatInputProps) {
  const [model, setModel] = useState(DEFAULT_MODEL)
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false)
  const { credits } = useCredits()

  const hasInsufficientCredits =
    credits !== null &&
    Number.isFinite(credits) &&
    credits < MIN_CREDITS_REQUIRED
  const isSendDisabled = !loading && hasInsufficientCredits
  const creditsTooltip = `You need at least $${MIN_CREDITS_REQUIRED.toFixed(
    2
  )} in credits to send messages. Top up in Settings.`
  const placeholder = isSendDisabled
    ? creditsTooltip
    : loading
      ? 'Waiting for agent...'
      : 'Ask anything...'

  // Memoize model groups to avoid recalculating on every render
  const openAIModels = useMemo(
    () =>
      Object.entries(languageModels).filter(
        ([_, m]) => m.provider === 'openai'
      ),
    []
  )

  const googleModels = useMemo(
    () =>
      Object.entries(languageModels).filter(
        ([_, m]) => m.provider === 'google'
      ),
    []
  )

  // Memoize submit handler
  const handleSubmit = useCallback(
    ({ text, files }: PromptInputMessage) => {
      if (hasInsufficientCredits) return
      const trimmedText = text?.trim()
      const hasFiles = files?.length > 0
      if (!trimmedText && !hasFiles) return
      onSend(model, trimmedText, files)
    },
    [hasInsufficientCredits, model, onSend]
  )

  // Memoize model selection handler
  const handleModelSelect = useCallback((key: string) => {
    setModel(key)
    setModelSelectorOpen(false)
  }, [])

  // Memoize stop click handler
  const handleStopClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      onStop?.()
    },
    [onStop]
  )

  return (
    <PromptInput className="mt-4" onSubmit={handleSubmit} multiple>
      <PromptInputAttachments>
        {(attachment) => <PromptInputAttachment data={attachment} />}
      </PromptInputAttachments>
      <PromptInputBody>
        <PromptInputTextarea placeholder={placeholder} disabled={loading} />
      </PromptInputBody>
      <PromptInputFooter>
        <PromptInputTools className="shrink-0 gap-0.5">
          <PromptInputActionMenu>
            <PromptInputActionMenuTrigger />
            <PromptInputActionMenuContent>
              <PromptInputActionAddAttachments />
            </PromptInputActionMenuContent>
          </PromptInputActionMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <PromptInputButton
                type="button"
                onClick={onToggleIncognito}
                disabled={loading}
                aria-pressed={isIncognito}
                variant={isIncognito ? 'default' : 'ghost'}
              >
                <IncognitoIcon className="size-4" />
              </PromptInputButton>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={6}>
              {isIncognito
                ? 'Incognito is on, chats are not saved'
                : 'Incognito is off'}
            </TooltipContent>
          </Tooltip>
          <ModelSelector
            onOpenChange={setModelSelectorOpen}
            open={modelSelectorOpen}
          >
            <ModelSelectorTrigger asChild>
              <PromptInputButton
                className="max-w-[150px]"
                title={languageModels[model].displayName}
              >
                <ModelSelectorLogo
                  provider={languageModels[model].provider}
                  className="shrink-0"
                />
                <ModelSelectorName className="truncate text-xs">
                  {languageModels[model].displayName}
                </ModelSelectorName>
              </PromptInputButton>
            </ModelSelectorTrigger>
            <ModelSelectorContent>
              <ModelSelectorInput placeholder="Search models..." />
              <ModelSelectorList>
                <ModelSelectorEmpty>No models found.</ModelSelectorEmpty>
                <ModelSelectorGroup heading="OpenAI">
                  {openAIModels.map(([key, m]) => {
                    return (
                      <ModelSelectorItem
                        key={key}
                        onSelect={() => handleModelSelect(key)}
                        value={key}
                      >
                        <ModelSelectorLogo provider={m.provider} />
                        <ModelSelectorName>{m.displayName}</ModelSelectorName>
                        <div className="ml-auto flex items-center gap-2">
                          {model === key && <CheckIcon className="size-4" />}
                        </div>
                      </ModelSelectorItem>
                    )
                  })}
                </ModelSelectorGroup>
                <ModelSelectorGroup heading="Google">
                  {googleModels.map(([key, m]) => (
                    <ModelSelectorItem
                      key={key}
                      onSelect={() => handleModelSelect(key)}
                      value={key}
                    >
                      <ModelSelectorLogo provider={m.provider} />
                      <ModelSelectorName>{m.displayName}</ModelSelectorName>
                    </ModelSelectorItem>
                  ))}
                </ModelSelectorGroup>
              </ModelSelectorList>
            </ModelSelectorContent>
          </ModelSelector>
        </PromptInputTools>
        {isSendDisabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex" tabIndex={0}>
                <PromptInputSubmit disabled />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={6}>
              {creditsTooltip}
            </TooltipContent>
          </Tooltip>
        ) : (
          <PromptInputSubmit
            status={loading ? 'streaming' : undefined}
            type={loading && onStop ? 'button' : 'submit'}
            onClick={loading && onStop ? handleStopClick : undefined}
          />
        )}
      </PromptInputFooter>
    </PromptInput>
  )
}
